import { useEffect, useRef, useState } from "react";
import { Lifebuoy, PaperPlaneRight, X, WhatsappLogo, Headset } from "@phosphor-icons/react";
import api from "../lib/api";

export const SupportBubble = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem("support_session_id") || null);
  const [escalation, setEscalation] = useState(null);
  const [showEscalateOffer, setShowEscalateOffer] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/support/sessions/${sessionId}`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {
        sessionStorage.removeItem("support_session_id");
        setSessionId(null);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, sending, escalation, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/support/chat", { message: text, session_id: sessionId });
      if (!sessionId) {
        setSessionId(data.session_id);
        sessionStorage.setItem("support_session_id", data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.escalate_suggested) setShowEscalateOffer(true);
    } catch (_e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — something went wrong. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    if (!sessionId) return;
    setSending(true);
    try {
      const { data } = await api.post("/support/escalate", { message: "Escalation requested", session_id: sessionId });
      setEscalation(data);
      setShowEscalateOffer(false);
    } catch (_e) {
      setMessages((m) => [...m, { role: "assistant", content: "Couldn't reach the team just now — please try again." }]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    sessionStorage.removeItem("support_session_id");
    setSessionId(null);
    setMessages([]);
    setEscalation(null);
    setShowEscalateOffer(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 left-6 z-40 h-14 w-14 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-white/15 text-white shadow-2xl flex items-center justify-center transition-all hover:scale-105"
        title="Traina support"
        data-testid="support-bubble-toggle"
      >
        <Lifebuoy size={24} weight="duotone" />
      </button>

      {open && (
        <div
          className="fixed bottom-24 left-6 z-50 w-[calc(100vw-3rem)] max-w-[400px] card-flat flex flex-col shadow-2xl"
          style={{ height: "min(540px, 75vh)" }}
          data-testid="support-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Headset size={18} weight="duotone" className="text-brand-light" />
              <div>
                <div className="font-display text-sm leading-none">Traina Support</div>
                <div className="tracker text-zinc-500 text-[9px] mt-1">AI answers instantly · humans on standby</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button onClick={reset} className="tracker text-[9px] text-zinc-500 hover:text-white" data-testid="support-new-chat">
                  New chat
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" data-testid="support-close">
                <X size={16} />
              </button>
            </div>
          </div>

          <div ref={threadRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="support-messages">
            {messages.length === 0 && (
              <p className="text-xs text-zinc-500 leading-relaxed">
                Hi! Ask me anything about Traina — billing, scheduling, imports, your clients' app, pricing… If I can't fix it, I'll hand you to a real person.
              </p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "ml-8 bg-brand/20 border border-brand/30 px-3 py-2" : "mr-8 bg-zinc-900 border border-white/10 px-3 py-2"}`}>
                {m.content}
              </div>
            ))}
            {sending && <div className="mr-8 text-zinc-500 text-xs animate-pulse">Traina Support is typing…</div>}

            {showEscalateOffer && !escalation && (
              <button onClick={escalate} className="btn-primary !py-2 !px-4 !text-xs w-full" data-testid="support-escalate-offer-btn">
                Connect me with the Traina team
              </button>
            )}

            {escalation && (
              <div className="border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid="support-escalation-card">
                <p className="text-xs text-emerald-300">
                  You're in the queue — the Traina team has been emailed your full conversation (ticket {escalation.ticket_id?.slice(0, 8)}).
                </p>
                {escalation.whatsapp_link && (
                  <a
                    href={escalation.whatsapp_link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white text-xs font-bold py-2 px-3 transition-colors"
                    data-testid="support-whatsapp-link"
                  >
                    <WhatsappLogo size={16} weight="fill" /> Continue on WhatsApp
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 space-y-2">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Describe your issue or question…"
                className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-zinc-600"
                data-testid="support-input"
              />
              <button onClick={send} disabled={sending || !input.trim()} className="btn-primary !py-2 !px-3 disabled:opacity-40" data-testid="support-send-btn">
                <PaperPlaneRight size={16} weight="fill" />
              </button>
            </div>
            {sessionId && !escalation && !showEscalateOffer && (
              <button onClick={escalate} className="tracker text-[9px] text-zinc-500 hover:text-zinc-300 w-full text-center" data-testid="support-talk-to-human">
                Talk to a human instead
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
